'use strict';

import { useMemo } from 'react';
import groceryCategories from '@app/tables/grocery-categories';

/**
 * Hook for categorizing and aggregating grocery data for display
 * 
 * @param {Array} groceries - Array of grocery objects
 * @returns {Object} Categorized groceries and totals
 */
export const useGroceryDisplay = (groceries) => {
    const categorized = useMemo(() => {
        if (!groceries || !Array.isArray(groceries)) {
            return { 
                fresh: [], 
                pantry: [],
                byCategory: {}
            };
        }

        // Get categories marked as fresh
        const freshCategories = groceryCategories
            .filter(c => c.fresh)
            .map(c => c.label);

        const fresh = groceries.filter(g => 
            freshCategories.includes(g.category)
        );
        
        const pantry = groceries.filter(g => 
            !freshCategories.includes(g.category)
        );

        // Group by actual category for detailed breakdown
        const byCategory = groceries.reduce((acc, grocery) => {
            const category = grocery.category || 'Other';
            if (!acc[category]) {
                acc[category] = [];
            }
            acc[category].push(grocery);
            return acc;
        }, {});

        // Group fresh items by category
        const freshByCategory = fresh.reduce((acc, grocery) => {
            const category = grocery.category || 'Other';
            if (!acc[category]) {
                acc[category] = [];
            }
            acc[category].push(grocery);
            return acc;
        }, {});

        // Group pantry items by category
        const pantryByCategory = pantry.reduce((acc, grocery) => {
            const category = grocery.category || 'Other';
            if (!acc[category]) {
                acc[category] = [];
            }
            acc[category].push(grocery);
            return acc;
        }, {});

        return { 
            fresh, 
            pantry, 
            byCategory,
            freshByCategory,
            pantryByCategory
        };
    }, [groceries]);

    const totals = useMemo(() => {
        if (!groceries || !Array.isArray(groceries)) {
            return {
                items: 0,
                price: 0,
                grams: 0,
                used_price: 0
            };
        }

        return {
            items: groceries.length,
            price: groceries.reduce((sum, g) => 
                sum + (typeof g.price === 'number' && !isNaN(g.price) ? g.price : 0), 0
            ),
            grams: groceries.reduce((sum, g) => 
                sum + (typeof g.grams_needed === 'number' && !isNaN(g.grams_needed) ? g.grams_needed : 0), 0
            ),
            used_price: groceries.reduce((sum, g) => 
                sum + (typeof g.used_price === 'number' && !isNaN(g.used_price) ? g.used_price : 0), 0
            )
        };
    }, [groceries]);

    const freshTotals = useMemo(() => {
        if (!categorized.fresh.length) {
            return {
                items: 0,
                price: 0,
                grams: 0,
                used_price: 0
            };
        }

        return {
            items: categorized.fresh.length,
            price: categorized.fresh.reduce((sum, g) => 
                sum + (typeof g.price === 'number' && !isNaN(g.price) ? g.price : 0), 0
            ),
            grams: categorized.fresh.reduce((sum, g) => 
                sum + (typeof g.grams_needed === 'number' && !isNaN(g.grams_needed) ? g.grams_needed : 0), 0
            ),
            used_price: categorized.fresh.reduce((sum, g) => 
                sum + (typeof g.used_price === 'number' && !isNaN(g.used_price) ? g.used_price : 0), 0
            )
        };
    }, [categorized.fresh]);

    const pantryTotals = useMemo(() => {
        if (!categorized.pantry.length) {
            return {
                items: 0,
                price: 0,
                grams: 0,
                used_price: 0
            };
        }

        return {
            items: categorized.pantry.length,
            price: categorized.pantry.reduce((sum, g) => 
                sum + (typeof g.price === 'number' && !isNaN(g.price) ? g.price : 0), 0
            ),
            grams: categorized.pantry.reduce((sum, g) => 
                sum + (typeof g.grams_needed === 'number' && !isNaN(g.grams_needed) ? g.grams_needed : 0), 0
            ),
            used_price: categorized.pantry.reduce((sum, g) => 
                sum + (typeof g.used_price === 'number' && !isNaN(g.used_price) ? g.used_price : 0), 0
            )
        };
    }, [categorized.pantry]);

    return { 
        categorized, 
        totals, 
        freshTotals, 
        pantryTotals 
    };
};